import debounce from 'lodash/debounce';
import { IN_LISTING_BANNERS_IDENTIFIER } from '@common/Listing/constants/InlistingBanners';
import {
    GA_EVENT_PROMOTION_VIEW,
    GA_EVENT_PROMOTION_CLICK,
    GA_EMAIL_PROMOTION_ID,
    GA_PROMOTION_ATTRIBUTE,
    GA_TRACKING_CREATIVE_ATTRIBUTE,
    GA_EVENT_NATIVE_PROMOTION_CLICK,
    GA_EVENT_NATIVE_PROMOTION_VIEW,
    TRACK_EMAIL_PROMO_SESSION_KEY,
    SALESFORCE_ON_RESPONSE_EVENT,
    GA4_EVENT_PROMOTION_VIEW,
    GA4_EVENT_PROMOTION_CLICK,
    assemblePromotionData,
    assemblePromoDataForApp,
    assembleGA4PromotionData,
    findPromotionBanners,
    sendEcEvent,
    trackBannerSnowplow
} from '@common/Tracking';
import { generateTrackingAttributes } from '@common/Tracking/helpers/GaHelpers';
import { pushEventToDataLayer } from '@helpers/dataLayer';
import { findAncestor } from '@helpers/findAncestor';
import { isInView } from '@helpers/isInView';
import { GA_EMAIL_PROMOTION_CREATIVE, GA_EMAIL_PROMOTION_NAME, GA_EMAIL_PROMOTION_POSITION } from '../constants/GaEcommerceKeys';

export const startPromotionTracking = (type, data) => {
    const appEvent = type === 'click' ? GA_EVENT_NATIVE_PROMOTION_CLICK : GA_EVENT_NATIVE_PROMOTION_VIEW;
    const dataForApp = assemblePromoDataForApp(data);
    sendEcEvent(appEvent, dataForApp);

    const event = type === 'click' ? GA_EVENT_PROMOTION_CLICK : GA_EVENT_PROMOTION_VIEW;
    const assembledData = assemblePromotionData(type, data);
    pushEventToDataLayer(event, assembledData.eventInfo, assembledData.eventData);

    const ga4assembledData = assembleGA4PromotionData(type, data);
    const ga4event = type === 'click' ? GA4_EVENT_PROMOTION_CLICK : GA4_EVENT_PROMOTION_VIEW;
    pushEventToDataLayer(ga4event, null, ga4assembledData);
};

const gaPromotionClick = (e) => {
    const isBanner = e.target.hasAttribute(GA_PROMOTION_ATTRIBUTE) || !!findAncestor(e.target, GA_PROMOTION_ATTRIBUTE);

    if (isBanner) {
        const banner = e.target.hasAttribute(GA_PROMOTION_ATTRIBUTE) ? e.target : findAncestor(e.target, GA_PROMOTION_ATTRIBUTE);

        if (banner.dataset?.attachedClickEvent) {
            return;
        }

        e.preventDefault();
        const bannerLinkEl = banner.hasAttribute('href') ? banner : findAncestor(e.target, 'cms-editor__link');
        startPromotionTracking('click', banner.dataset);
        trackBannerSnowplow('click', banner.dataset);

        banner.dataset.attachedClickEvent = '1';

        if ((bannerLinkEl && bannerLinkEl.hasAttribute('href')) || e.target.href) {
            window.location.href = (bannerLinkEl && bannerLinkEl.hasAttribute('href'))
                ? bannerLinkEl.getAttribute('href') : e.target.href;
        }
    }
};

const gaPromotionView = (promotionElements) => {
    const inViewData = [];
    const notVisible = [];

    promotionElements.forEach((elm) => {
        if (isInView(elm, 2)) {
            inViewData.push(elm.dataset);
        } else {
            notVisible.push(elm);
        }
    });

    if (inViewData.length > 0) {
        startPromotionTracking('view', inViewData);
        trackBannerSnowplow('view', inViewData);
    }

    return notVisible;
};

const trackEmailPromotionClick = () => {
    function getQueryParamValue(queryParams, name) {
        const promoIdParam = queryParams.find(param => param.indexOf(name) !== -1);
        return promoIdParam ? promoIdParam.replace(`${name}=`, '') : '';
    }

    const { search } = window.location;
    const haveTrackedEmailPromo = sessionStorage.getItem(TRACK_EMAIL_PROMO_SESSION_KEY);

    if (search.indexOf(GA_EMAIL_PROMOTION_ID) !== -1 && !haveTrackedEmailPromo) {
        const queryParams = decodeURIComponent(search.replace('?', '')).split('&');

        const promoIdParam = getQueryParamValue(queryParams, GA_EMAIL_PROMOTION_ID);
        const promoNameParam = getQueryParamValue(queryParams, GA_EMAIL_PROMOTION_NAME);
        const promoCreativeParam = getQueryParamValue(queryParams, GA_EMAIL_PROMOTION_CREATIVE);
        const promoPositionParam = getQueryParamValue(queryParams, GA_EMAIL_PROMOTION_POSITION);

        const assembledData = assemblePromotionData('click', [], [{
            id: promoIdParam,
            name: promoNameParam,
            creative: promoCreativeParam,
            position: promoPositionParam
        }]);

        assembledData.eventInfo.label = promoIdParam;

        pushEventToDataLayer(GA_EVENT_PROMOTION_CLICK, assembledData.eventInfo, assembledData.eventData);

        sessionStorage.setItem(TRACK_EMAIL_PROMO_SESSION_KEY, true);
    }
};

export const initPromotionTracking = (parentNode, selector) => {
    const {
        controller = '',
        action = ''
    } = document.body.dataset;

    if (controller === 'listing' && action === 'list') {
        generateTrackingAttributes();
    }

    const banners = findPromotionBanners(parentNode, selector);
    const overflowXBlocks = document.querySelectorAll(
        '.grid-content--multi-images--3, .grid-content--multi-images--4, .cms-product-template'
    );

    let promotionElements = banners;

    const trackPromotionsInView = debounce(() => {
        promotionElements = gaPromotionView(promotionElements);

        if (promotionElements.length <= 0) {
            if (overflowXBlocks.length > 0) {
                for (const block of overflowXBlocks) {
                    block.removeEventListener('scroll', trackPromotionsInView);
                }
            }

            window.removeEventListener('scroll', trackPromotionsInView);
        }
    });

    if (banners.length > 0) {
        trackPromotionsInView();

        if (overflowXBlocks.length > 0) {
            for (const block of overflowXBlocks) {
                block.addEventListener('scroll', trackPromotionsInView);
            }
        }

        window.addEventListener('scroll', trackPromotionsInView);
        document.addEventListener('click', e => gaPromotionClick(e));
    }

    trackEmailPromotionClick();
};

const initMcpBannerTracking = () => {
    let mcpBanners = [];

    const trackMcpBannersInView = debounce(() => {
        mcpBanners = gaPromotionView(mcpBanners);

        if (mcpBanners.length <= 0) {
            window.removeEventListener('scroll', trackMcpBannersInView);
        }
    });

    document.addEventListener(
        SALESFORCE_ON_RESPONSE_EVENT,
        () => setTimeout(() => {
            mcpBanners = findPromotionBanners(
                document,
                `[${GA_TRACKING_CREATIVE_ATTRIBUTE}="mcp"]`
            );

            if (mcpBanners.length > 0) {
                window.addEventListener('scroll', trackMcpBannersInView);
            }
        }, 100)
    );
};

export const trackGaPromotions = () => {
    // Ignore dynamically injected elements since they have its own tracking implementation
    const trackingSelector = `[${GA_PROMOTION_ATTRIBUTE}]:not(.${IN_LISTING_BANNERS_IDENTIFIER} [${GA_PROMOTION_ATTRIBUTE}])`;

    window.addEventListener('load', () => {
        initPromotionTracking(document, trackingSelector);
        initMcpBannerTracking();
    }, { once: true });
};
